import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ModulePageType } from 'module/page/model/module-page.type';

import { Article, IArticle } from '../../article/article';
import { Document, IDocument } from '../../document/document';
import { IModuleGraph, ModuleGraph } from './graph/module-graph';
import { IModulePageI18n, ModulePageI18n } from './module-page-i18n';
import { IModuleQuestion, ModuleQuestion } from './question/module-question';
import { IModuleTask, ModuleTask } from './task/module-task';

export interface IModulePage extends IEntity {
	readonly position: number;
	readonly type: ModulePageType;
	readonly documentsId: string[];
	readonly tasksId: ReadonlyArray<string>;
	readonly graphsId: ReadonlyArray<string>;
	readonly localizations: IModulePageI18n[];
	readonly articlesId: ReadonlyArray<string>;
	readonly questionsId: ReadonlyArray<string>;
	readonly documents$: Observable<IDocument[]>;
	readonly localization$: Observable<IModulePageI18n>;
	readonly tasks$: Observable<ReadonlyArray<IModuleTask>>;
	readonly articles$: Observable<ReadonlyArray<IArticle>>;
	readonly graphs$: Observable<ReadonlyArray<IModuleGraph>>;
	readonly questions$: Observable<ReadonlyArray<IModuleQuestion>>;
	readonly hasSettings: boolean;

	observeQuestions(excludeQuestionsId: string[]): Observable<IModuleQuestion[]>;
	observeQuestion(questionId: string): Observable<IModuleQuestion>;
}

export class ModulePage extends Entity implements IModulePage {
	private _position: number;
	private _type: ModulePageType;
	private _tasksId: string[];
	private _graphsId: string[];
	private _articlesId: string[];
	private _questionsId: string[];
	private _documentsId: string[];
	private _localizations: ModulePageI18n[];
	private _tasks$: Observable<ModuleTask[]>;
	private _articles$: Observable<Article[]>;
	private _graphs$: Observable<ModuleGraph[]>;
	private _documents$: Observable<Document[]>;
	private _questions$: Observable<ModuleQuestion[]>;
	private _localization$: Observable<ModulePageI18n>;

	constructor(
		id: string,
		position: number,
		type: ModulePageType,
		updateTimestamp: number,
		localizations: ModulePageI18n[],
		tasksId: string[] = [],
		graphsId: string[] = [],
		articlesId: string[] = [],
		questionsId: string[] = [],
		documentsId: string[] = [],
		tasks$: Observable<ModuleTask[]> = of([]),
		articles$: Observable<Article[]> = of([]),
		graphs$: Observable<ModuleGraph[]> = of([]),
		questions$: Observable<ModuleQuestion[]> = of([]),
		documents$: Observable<Document[]> = of([]),
		localization$: Observable<ModulePageI18n> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._position = position;
		this._type = type;
		this._documentsId = documentsId;
		this._localizations = localizations;
		this._tasksId = tasksId;
		this._graphsId = graphsId;
		this._articlesId = articlesId;
		this._questionsId = questionsId;
		this._documents$ = documents$;
		this._localization$ = localization$;
		this._tasks$ = tasks$;
		this._graphs$ = graphs$;
		this._articles$ = articles$;
		this._questions$ = questions$;
	}

	public observeQuestions(
		excludeQuestionsId: string[]
	): Observable<ModuleQuestion[]> {
		return this._questions$.pipe(
			map((questions) =>
				questions.filter(
					(question) => !(excludeQuestionsId || []).includes(question.id)
				)
			)
		);
	}

	public observeQuestion(questionId: string): Observable<IModuleQuestion> {
		return this._questions$.pipe(
			map((questions) => questions.find((question) => questionId === question.id))
		);
	}

	public get position(): number {
		return this._position;
	}

	public get type(): ModulePageType {
		return this._type;
	}

	public get articlesId(): string[] {
		return this._articlesId;
	}

	public get documentsId(): string[] {
		return this._documentsId;
	}

	public get localizations(): ModulePageI18n[] {
		return this._localizations;
	}

	public get tasksId(): string[] {
		return this._tasksId;
	}

	public get graphsId(): string[] {
		return this._graphsId;
	}

	public get questionsId(): string[] {
		return this._questionsId;
	}

	public get documents$(): Observable<Document[]> {
		return this._documents$;
	}

	public get localization$(): Observable<ModulePageI18n> {
		return this._localization$;
	}

	public get tasks$(): Observable<ModuleTask[]> {
		return this._tasks$;
	}

	public get articles$(): Observable<Article[]> {
		return this._articles$;
	}

	public get graphs$(): Observable<ModuleGraph[]> {
		return this._graphs$;
	}

	public get questions$(): Observable<ModuleQuestion[]> {
		return this._questions$;
	}

	public get hasSettings(): boolean {
		switch (this.type) {
			case ModulePageType.LINK:
				return true;
			case ModulePageType.GENERAL:
			case ModulePageType.DOCUMENTS:
				return false;
			default:
				return false;
		}
	}
}
